import { Dispatch, createContext, useCallback, useContext, useEffect, useMemo, useReducer, useState } from "react";

import { LoginService } from "./../services/LoginService";
import { Actions, ILoginFormState, initialState, reducer } from "./reducers/AuthReducer";

interface IAuthContextData {
    state: ILoginFormState;
    dispatch: Dispatch<Actions>;
    logout: () => void;
    isAuthenticated: boolean;
    userName: string;
    userCode: number | undefined;
    login: (usuario: string, senha: string) => Promise<string | void>;
}

export const APP_ACCESS_TOKEN__LOCALSTORAGE_KEY = "APP_ACCESS_TOKEN";
export const APP_USER_NAME__LOCALSTORAGE_KEY = "APP_USER_NAME";
export const APP_USER_CODE__LOCALSTORAGE_KEY = "APP_USER_CODE";

const AuthContext = createContext({} as IAuthContextData);

interface IAuthProviderProps {
    children: React.ReactNode;
}

export const AuthProvider: React.FC<IAuthProviderProps> = ({ children }) => {

    const [accessToken, setAccessToken] = useState<string>();
    const [userNameAuthenticated, setUserNameAuthenticated] = useState<string>('');
    const [userCodeAuthenticated, setUserCodeAuthenticated] = useState<number | undefined>();
    const [state, dispatch] = useReducer(reducer, initialState);

    //Método criado para quando houver um refresh na tela (F5 ou Crtl+R)
    useEffect(() => {
        const accessTokenLocal = localStorage.getItem(APP_ACCESS_TOKEN__LOCALSTORAGE_KEY);
        const userName = localStorage.getItem(APP_USER_NAME__LOCALSTORAGE_KEY);
        const userCode = localStorage.getItem(APP_USER_CODE__LOCALSTORAGE_KEY);

        if (accessTokenLocal) {
            setAccessToken(JSON.parse(accessTokenLocal));
        } else {
            setAccessToken(undefined);
        }

        if (userName) {
            setUserNameAuthenticated(JSON.parse(userName));
        } else {
            setUserNameAuthenticated('');
        }

        if (userCode) {
            setUserCodeAuthenticated(JSON.parse(userCode));
        } else {
            setUserCodeAuthenticated(undefined);
        }
    }, []);

    const handleLogin = useCallback(async (usuario: string, senha: string) => {
        const result = await LoginService.postLogin(usuario, senha);

        if (result instanceof Error) {
            return result.message;
        } else {
            localStorage.setItem(APP_ACCESS_TOKEN__LOCALSTORAGE_KEY, JSON.stringify(result.token))
            localStorage.setItem(APP_USER_NAME__LOCALSTORAGE_KEY, JSON.stringify(result.nome))
            localStorage.setItem(APP_USER_CODE__LOCALSTORAGE_KEY, JSON.stringify(result.codigoPessoFisica))
            setAccessToken(result.token);
            setUserNameAuthenticated(result.nome);
            setUserCodeAuthenticated(result.codigoPessoFisica);
            return undefined;
        }

    }, []);

    const handleLogout = useCallback(() => {
        setAccessToken(undefined);
        localStorage.clear();
    }, []);

    const isAuthenticated = useMemo(() => accessToken !== undefined, [accessToken]);
    const userName = useMemo(() => userNameAuthenticated, [userNameAuthenticated]);
    const userCode = useMemo<number | undefined>(() => userCodeAuthenticated, [userCodeAuthenticated]);

    return (
        <AuthContext.Provider value={{ state, dispatch, isAuthenticated, login: handleLogin, logout: handleLogout, userName, userCode }}>
            {children}
        </AuthContext.Provider>

    );
};

export const useAuthContext = () => useContext(AuthContext);

